import type {
  CreateDocumentShareRequest,
  DocumentShareDto,
  DocumentShareListItemDto,
  RejectDocumentShareRequest,
  ValidateEmailResponse,
} from '@tracedocuments/types';

import { ApiError, apiFetch } from './fetch';
import { apiFetchWithTimeout } from './fetch-timeout';

export const SHARE_ACCEPTANCE_EXPIRED = 'SHARE_ACCEPTANCE_EXPIRED';

async function readApiErrorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    return (body.error ?? body.message ?? '').trim();
  } catch {
    return '';
  }
}

async function throwShareActionError(res: Response, fallback: string): Promise<never> {
  const message = await readApiErrorMessage(res);
  throw new ApiError(message || fallback, res.status);
}

export async function validateRecipientEmail(email: string): Promise<ValidateEmailResponse> {
  const res = await apiFetch('/api/users/validate-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('validate-email failed');
  return res.json();
}

export async function createDocumentShare(
  request: CreateDocumentShareRequest,
): Promise<DocumentShareDto> {
  const res = await apiFetch('/api/document-shares', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error('create share failed');
  return res.json();
}

export async function fetchShareInbox(
  page = 1,
  pageSize = 50,
): Promise<DocumentShareListItemDto[]> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const res = await apiFetchWithTimeout(`/api/document-shares/inbox?${params}`);

  if (!res.ok) throw new Error('inbox failed');
  const data = await res.json();
  if (data && typeof data === 'object' && Array.isArray(data.items)) {
    return data.items as DocumentShareListItemDto[];
  }
  return data as DocumentShareListItemDto[];
}

export async function fetchShareSent(
  page = 1,
  pageSize = 50,
  archived = false,
): Promise<DocumentShareListItemDto[]> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    archived: String(archived),
  });
  const res = await apiFetchWithTimeout(`/api/document-shares/sent?${params}`);

  if (!res.ok) {
    throw new Error(`sent shares failed (${res.status})`);
  }
  const data = await res.json();
  if (data && typeof data === 'object' && Array.isArray(data.items)) {
    return data.items as DocumentShareListItemDto[];
  }
  return data as DocumentShareListItemDto[];
}

export async function fetchPendingShareCount(): Promise<number> {
  const res = await apiFetchWithTimeout('/api/document-shares/inbox/pending-count');
  if (!res.ok) return 0;
  const data = (await res.json()) as { count: number };
  return data.count ?? 0;
}

export async function fetchDocumentShare(id: string): Promise<DocumentShareDto> {
  const res = await apiFetchWithTimeout(`/api/document-shares/${id}`, undefined, 20_000);
  if (!res.ok) throw new Error('share not found');
  return res.json();
}

export async function acceptDocumentShare(id: string): Promise<DocumentShareDto> {
  const res = await apiFetch(`/api/document-shares/${id}/accept`, { method: 'POST' });
  if (!res.ok) await throwShareActionError(res, 'accept failed');
  return res.json();
}

export async function rejectDocumentShare(
  id: string,
  request: RejectDocumentShareRequest = {},
): Promise<DocumentShareDto> {
  const res = await apiFetch(`/api/document-shares/${id}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) await throwShareActionError(res, 'reject failed');
  return res.json();
}

export async function revokeDocumentShare(id: string): Promise<DocumentShareDto> {
  const res = await apiFetch(`/api/document-shares/${id}/revoke`, { method: 'POST' });
  if (!res.ok) throw new Error('revoke failed');
  return res.json();
}

export async function archiveSentShare(id: string): Promise<void> {
  const res = await apiFetch(`/api/document-shares/${id}/archive-sent`, { method: 'POST' });
  if (!res.ok) throw new Error('archive share failed');
}

export async function unarchiveSentShare(id: string): Promise<void> {
  const res = await apiFetch(`/api/document-shares/${id}/unarchive-sent`, { method: 'POST' });
  if (!res.ok) throw new Error('unarchive share failed');
}

export async function fetchDocumentShareView(
  id: string,
  page?: number,
  init?: RequestInit,
): Promise<Response> {
  const query = page != null ? `?page=${page}` : '';
  // Large PDFs need headroom, but unbounded wait leaves mobile spinners forever.
  return apiFetchWithTimeout(`/api/document-shares/${id}/view${query}`, init, 60_000);
}

export type DocumentShareSecurityEventType = 'view_open' | 'capture_attempt' | 'print_attempt';

export async function reportDocumentShareSecurityEvent(
  shareId: string,
  sessionId: string,
  eventType: DocumentShareSecurityEventType,
): Promise<void> {
  const res = await apiFetch(`/api/document-shares/${shareId}/security-events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, eventType }),
  });
  if (!res.ok) throw new Error('security event failed');
}

export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return star[1].trim().replace(/^"|"$/g, '');
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header) ?? /filename=([^;]+)/i.exec(header);
  return plain?.[1]?.trim().replace(/^"|"$/g, '') ?? null;
}
